/**
 * As 7 famílias de cor de status da repaginação (README §2 e quadro 3a da
 * proposta). Toda cor de status do sistema sai daqui: o mapa enum → família
 * mora em lib/ui/status-colors.ts (`*_FAMILY`), a pill em
 * components/ds/StatusPill.tsx e as cores em app/globals.css
 * (--status-<família>, -dot, -ink; claro e escuro).
 *
 * A família é escolhida pelo SIGNIFICADO, nunca pelo gosto:
 */
export type StatusFamily =
  | "gray"
  | "blue"
  | "purple"
  | "orange"
  | "yellow"
  | "green"
  | "red";

export const STATUS_FAMILIES: readonly StatusFamily[] = [
  "gray",
  "blue",
  "purple",
  "orange",
  "yellow",
  "green",
  "red",
];

/** Regra de significado (DECISOES.md, D5). */
export const STATUS_FAMILY_MEANING: Record<StatusFamily, string> = {
  gray: "Inativo ou não começou",
  blue: "Aguardando outra pessoa",
  purple: "Pronto para começar",
  orange: "Data marcada / prazo chegando",
  yellow: "Em execução",
  green: "Concluído / positivo",
  red: "Bloqueio, atraso, recusa",
};

/** Nome pt-BR da família (galeria /dev/componentes, documentação). */
export const STATUS_FAMILY_LABEL: Record<StatusFamily, string> = {
  gray: "Cinza",
  blue: "Azul",
  purple: "Roxo",
  orange: "Laranja",
  yellow: "Amarelo",
  green: "Verde",
  red: "Vermelho",
};

/**
 * Classes por família — escritas por extenso para o Tailwind encontrar
 * (classe montada com template string não gera CSS).
 */
export const STATUS_FAMILY_PILL: Record<StatusFamily, string> = {
  gray: "bg-status-gray text-status-gray-ink",
  blue: "bg-status-blue text-status-blue-ink",
  purple: "bg-status-purple text-status-purple-ink",
  orange: "bg-status-orange text-status-orange-ink",
  yellow: "bg-status-yellow text-status-yellow-ink",
  green: "bg-status-green text-status-green-ink",
  red: "bg-status-red text-status-red-ink",
};

export const STATUS_FAMILY_DOT: Record<StatusFamily, string> = {
  gray: "bg-status-gray-dot",
  blue: "bg-status-blue-dot",
  purple: "bg-status-purple-dot",
  orange: "bg-status-orange-dot",
  yellow: "bg-status-yellow-dot",
  green: "bg-status-green-dot",
  red: "bg-status-red-dot",
};

/** Cor do texto da família sem a pill (valor em tabela, indicador em texto). */
export const STATUS_FAMILY_TEXT: Record<StatusFamily, string> = {
  gray: "text-status-gray-ink",
  blue: "text-status-blue-ink",
  purple: "text-status-purple-ink",
  orange: "text-status-orange-ink",
  yellow: "text-status-yellow-ink",
  green: "text-status-green-ink",
  red: "text-status-red-ink",
};

/**
 * Borda na cor do ponto da família — escritas por extenso pelo mesmo motivo
 * das outras. Usada pelo halo do anel de progresso, que acende quando a obra
 * tem novidade não vista (lib/ui/obra-novidades).
 */
export const STATUS_FAMILY_BORDER: Record<StatusFamily, string> = {
  gray: "border-status-gray-dot",
  blue: "border-status-blue-dot",
  purple: "border-status-purple-dot",
  orange: "border-status-orange-dot",
  yellow: "border-status-yellow-dot",
  green: "border-status-green-dot",
  red: "border-status-red-dot",
};

/**
 * A cor do ponto como TINTA (`currentColor`) — escritas por extenso pelo mesmo
 * motivo das outras. É assim que o halo do anel de progresso carrega a cor da
 * família: a borda sai em `border-current` e a onda do pulso
 * (`animate-anel-novidade`, em app/globals.css) nasce de `currentColor`, sem
 * uma regra por família no CSS.
 */
export const STATUS_FAMILY_DOT_TEXT: Record<StatusFamily, string> = {
  gray: "text-status-gray-dot",
  blue: "text-status-blue-dot",
  purple: "text-status-purple-dot",
  orange: "text-status-orange-dot",
  yellow: "text-status-yellow-dot",
  green: "text-status-green-dot",
  red: "text-status-red-dot",
};

/**
 * Cor do ponto de cada família em hex (tema claro), para quem precisa de cor
 * concreta fora do CSS — ex.: o pin SVG do mapa de obras. Espelha
 * --status-<família>-dot do globals.css (um teste garante).
 */
export const STATUS_FAMILY_DOT_HEX: Record<StatusFamily, string> = {
  gray: "#9b9797",
  blue: "#3f74a8",
  purple: "#7856c2",
  orange: "#e0701a",
  yellow: "#caa000",
  green: "#2e9148",
  red: "#ec3013",
};

/** O que o <Badge> desenha: uma família, uma tag, ou só texto. */
export type BadgeKind = StatusFamily | "tag" | "plain" | "strong";

/**
 * Toda variante aceita pelo <Badge> → o que ela desenha. Inclui os nomes
 * antigos (de antes das 7 famílias), que continuam válidos — há valores
 * gravados no banco (seções do catálogo de orçamento). Cada nome antigo vai
 * para a família do seu significado: success = concluído, danger = bloqueio,
 * info = aguardando, warning = atenção/prazo (laranja), neutral = inativo; os
 * matizes sem família própria caem na mais próxima; marcas que não são status
 * ("Mais vendida", "Padrão", "IA") viram tag.
 */
export const BADGE_VARIANT_KIND = {
  gray: "gray",
  blue: "blue",
  purple: "purple",
  orange: "orange",
  yellow: "yellow",
  green: "green",
  red: "red",
  tag: "tag",
  plain: "plain",
  strong: "strong",
  success: "green",
  warning: "orange",
  danger: "red",
  info: "blue",
  neutral: "gray",
  teal: "green",
  pink: "purple",
  brown: "orange",
  accent: "tag",
  ai: "tag",
} as const satisfies Record<string, BadgeKind>;

export type BadgeVariantName = keyof typeof BADGE_VARIANT_KIND;

/** Família de uma variante do <Badge>; null quando não é status. */
export function badgeVariantFamily(variant: BadgeVariantName): StatusFamily | null {
  const kind: BadgeKind = BADGE_VARIANT_KIND[variant] ?? "gray";
  return kind === "tag" || kind === "plain" || kind === "strong" ? null : kind;
}

/**
 * Nome antigo de variante do <Badge> para cada família (1 para 1).
 *
 * Os `*_VARIANT` de status-colors.ts continuam existindo enquanto houver tela
 * não migrada, e agora são DERIVADOS da família por este mapa. Ficam com os
 * nomes antigos (e não "green", "blue"…) porque há código que lê a variante
 * para outras coisas — cor do pin do mapa de obras, tom do marcador do Gantt,
 * severidade de cluster — e conhece só esses nomes. O <Badge> desenha cada
 * nome antigo com a família correspondente.
 */
export const FAMILY_LEGACY_VARIANT = {
  gray: "neutral",
  blue: "info",
  purple: "purple",
  orange: "warning",
  yellow: "yellow",
  green: "success",
  red: "danger",
} as const satisfies Record<StatusFamily, string>;

export type FamilyLegacyVariant =
  (typeof FAMILY_LEGACY_VARIANT)[StatusFamily];

/** `*_VARIANT` derivado de um `*_FAMILY`. */
export function familyVariants<K extends string>(
  families: Record<K, StatusFamily>,
): Record<K, FamilyLegacyVariant> {
  const out = {} as Record<K, FamilyLegacyVariant>;
  for (const key of Object.keys(families) as K[]) {
    out[key] = FAMILY_LEGACY_VARIANT[families[key]];
  }
  return out;
}

/**
 * Igual a `familyVariants`, para mapas em que alguns valores não têm família
 * (não são status — ex.: "Sem aprovação", cliente "Ativo"): esses viram texto
 * simples no <Badge>.
 */
export function nullableFamilyVariants<K extends string>(
  families: Record<K, StatusFamily | null>,
): Record<K, FamilyLegacyVariant | "plain"> {
  const out = {} as Record<K, FamilyLegacyVariant | "plain">;
  for (const key of Object.keys(families) as K[]) {
    const family = families[key];
    out[key] = family ? FAMILY_LEGACY_VARIANT[family] : "plain";
  }
  return out;
}
